import { createObjectID, isAllEmpty, omitDeep, regexps } from "@bonliva-crm/common";
import { CustomerRefInput } from "@bonliva-crm/graphql";
import locale from "@bonliva-crm/locale";
import {
  FileUploadConfig,
  GDPRCommunicationChannel,
  GDPRConsentType,
  GDPROptionType,
  Professions,
  SelfRegisterOption
} from "@bonliva-crm/shared";
import { BisnodeLookupStore, OkCancelDialogOptions } from "@containers";
import {
  ConsultantCardFragment,
  ConsultantEditor_FindConsultantDuplicate,
  ConsultantEditor_GetDepartments,
  ConsultantEditor_ImportCV,
  ConsultantEmergencyContactsSectionItemInput,
  GDPRInput,
  InitialContact,
  LegalPersonType,
  ConsultantEditor_GetForm as getForm,
  ConsultantEditor_Submit as submitForm
} from "@graphql";
import { FormStore } from "@modules/formx";
import { ConsultantProfileLocation, Navigation } from "@navigation";
import { store } from "@store";
import { compact, debounce, get, isEmpty, set, sortBy } from "lodash";
import { action, computed, observable, runInAction } from "mobx";
import { ConsultantCVStore } from "./ConsultantCVStore";
import { ProfessionSelectionStore } from "./components/ProfessionSelectionStore";
import { applyLookupResult } from "./utils";
import { validationSchema } from "./validationSchema";
import {
  ConsultantFormData,
  initialFormData,
  prepareDataForSubmit
} from "@consultants/ConsultantEditor/consultant-edit-form";
import { ExpertiseProfessionStore } from "@consultants/ConsultantEditor/components/ExpertiseProfessionStore";
import { createProfessionStore } from "@consultants/ConsultantEditor/components/expertise-profession-store-factory";

export class ConsultantEditorStore {
  get allCountries() {
    return store().cache.countries;
  }

  get allCountriesWorldwide() {
    return store().cache.allCountriesWorldwide.slice();
  }

  get allLanguages() {
    return store().cache.languages.slice();
  }

  get mainLanguages() {
    return store().cache.mainLanguages.slice();
  }

  get allAreasOfExp() {
    return store()
      .cache.areasOfExp.map((a) => a.Name)
      .slice();
  }

  get allCompetitors() {
    return store().cache.competitors.slice();
  }

  get allProfessions() {
    return store().cache.professions;
  }

  @computed get isLookingUp() {
    return this.bisnodeLookup.isLoading;
  }

  @computed get canLookupSeSSN() {
    const value = (this.form.values.personal?.ssn || "").trim();
    return Boolean(value && regexps.ssn.test(value));
  }

  @computed get canViewAccountNumber() {
    return store().user.can("consultant-account-details:read:all");
  }

  @computed get canEditAccountNumber() {
    return store().user.can("consultant-account-details:update:all");
  }

  @computed get canLookupNoSSN() {
    const value = (this.form.values.personal?.norwegianSsn || "").trim();
    return Boolean(value && regexps.ssn.test(value));
  }

  @computed get showNorwegianSSN() {
    return this.form.values.personal?.countries?.includes("Norway");
  }

  @computed get hasDoctorProfession() {
    return !!this.form.values.professions.find(
      (x) => x.professionName === Professions.NO_LEGE
    );
  }

  @computed get initialContact() {
    return this.form.values.contacts?.initialContact;
  }

  @computed get selfRegisterOption() {
    return this.form.values.contacts?.selfRegisterOption;
  }

  @computed get canUpdateInitialContact() {
    return store().user.hasPermission("CanUpdateConsultantInitialContact");
  }

  @computed get canDelete() {
    return store().user.hasPermission("CanDeleteConsultants");
  }

  @computed get recommendedBy() {
    return this.form.values.contacts?.recommendedByConsultant
      ? // when loaded from the server, the value is the consultant ref (with Name),
        // not the { label, value } option shape declared on the input type
        (this.form.values.contacts?.recommendedByConsultant as { Name?: string }).Name
      : locale.missing;
  }

  @computed get cellPhone() {
    return this.form.values.contacts?.cellPhone;
  }

  @computed get phone() {
    return this.form.values.contacts?.phone;
  }

  @computed get recommendedByConsultantValue() {
    return this.form.values.contacts?.recommendedByConsultant;
  }

  @computed get languageItems() {
    return this.form.values.languageSkills?.items;
  }

  @computed get educationItems() {
    return this.form.values.education?.items;
  }

  @computed get workExperienceItems() {
    return this.form.values.workExperience?.items;
  }

  @computed get referencesItems() {
    return this.form.values.references?.items;
  }

  @computed get emergencyContactsItems() {
    return this.form.values.emergencyContacts?.items;
  }

  @computed get legalPersonType() {
    return this.form.values.contactType?.type;
  }

  @computed get travelPreferences() {
    return this.form.values.personal?.travelPreferences;
  }

  @computed get accommodationPreferences() {
    return this.form.values.personal?.accommodationPreferences;
  }

  @computed get care() {
    return this.form.values.personal?.care;
  }

  @computed get clearingNumber() {
    return this.form.values.contactType?.clearingNumber;
  }

  @computed get emergencyContactErrors() {
    return this.form.errors.emergencyContacts;
  }

  @computed get isFileSelected() {
    return !!this.file;
  }

  @computed get isMarketingConsentEnabled() {
    return (
      this.marketingConsentOption &&
      this.marketingConsentOption !== GDPROptionType.NotSpecified
    );
  }

  @computed get isMailingEmailConsentEnabled() {
    return (
      this.mailingEmailConsentOption &&
      this.mailingEmailConsentOption !== GDPROptionType.NotSpecified
    );
  }

  @computed get isMailingSmsConsentEnabled() {
    return (
      this.mailingSmsConsentOption &&
      this.mailingSmsConsentOption !== GDPROptionType.NotSpecified
    );
  }

  get showInitialCommentField() {
    return this.isNew;
  }

  get hasErrorsInPersonalTab() {
    const { errors } = this.form;

    return (
      !isAllEmpty(
        errors.personal,
        errors.address,
        errors.contacts,
        errors.GDPRConsentRecords
      ) || this.isSsnDuplicated
    );
  }

  get hasErrorsInBackgroundTab() {
    const { errors } = this.form;

    return !isAllEmpty(errors.education, errors.workExperience, errors.languageSkills);
  }

  get hasErrorsInExpertiseTab() {
    const { errors } = this.form;

    return !isAllEmpty(errors.expertise);
  }

  get hasErrorsInEconomyTab() {
    const { errors } = this.form;

    return !isAllEmpty(errors.contactType, errors.employeeNumber);
  }

  get hasErrorsInMiscTab() {
    const { errors } = this.form;

    return !isAllEmpty(errors.emergencyContacts, errors.competitors);
  }

  get isSsnDuplicated() {
    return (
      this.contactsValidate?.duplicate?.SocialSecurityNo === this.form.values.personal.ssn
    );
  }

  @observable isLoading = true;
  @observable isFileProcessing = false;
  @observable file: File | null = null;
  @observable processedFile: File | null = null;
  @observable error = null;
  @observable marketingConsentOption = null;
  @observable mailingEmailConsentOption = null;
  @observable mailingSmsConsentOption = null;
  @observable marketingConsentChannel = null;
  @observable mailingEmailConsentChannel = null;
  @observable mailingSmsConsentChannel = null;
  @observable
  allDepartments: ConsultantEditor_GetDepartments.Return["departments"]["items"] = [];
  @observable contactsValidate = {
    duplicate: null,
    similar: null
  };

  importCvUploadConfig = new FileUploadConfig();

  // The explicit annotation is load-bearing: without it, the initializer's
  // callbacks (`() => this.submit()` etc.) make the property type self-referential
  // and TS silently widens `form` to `any` (strict is off).
  form: FormStore<ConsultantFormData> = new FormStore<ConsultantFormData>({
    onSubmit: () => this.submit(),
    schema: validationSchema,
    debounceValidationMs: 250,
    validate: () => this.customValidate()
  });

  isNew = false;

  professionStores: Record<string, ExpertiseProfessionStore> = {};

  bisnodeLookup = new BisnodeLookupStore();

  readonly professionSelectionStore: ProfessionSelectionStore;
  readonly consultantCVManager: ConsultantCVStore;

  constructor(private consultantId: string, private navigation: Navigation) {
    this.init().catch((err) => {
      store().snackbars.error(`Cannot load form\nReason: ${err}`);
    });

    this.professionSelectionStore = new ProfessionSelectionStore(this);
    this.consultantCVManager = new ConsultantCVStore(consultantId, this);

    if (!consultantId) {
      this.isNew = true;
    }

    const debouncedCheck = debounce((event) => {
      this.checkContactsOnDuplicating(event);
    }, 400);
    this.form.onSetValue(debouncedCheck);
    (window as any).consultantEditorStore = this;
  }

  getAOEsOnProfession(profession: string) {
    return sortBy(
      store()
        .cache.getAreaOfExpOnProfessions([profession])
        .map((x) => x.Name)
    );
  }

  getSpecialization(expName: string, profession: string) {
    return sortBy(store().cache.getSpecializationsOfExpertise(expName, profession));
  }

  getSystemsOnProfession(profession: string) {
    return sortBy(store().cache.getSystemsOnProfession(profession));
  }

  getMachinesOnProfession(profession: string) {
    return sortBy(store().cache.getMachinesOnProfession(profession));
  }

  getEducationsOnProfession(profession: string) {
    return sortBy(store().cache.getEducationsOnProfession(profession));
  }

  getMethodsOnProfession(profession: string) {
    return sortBy(store().cache.getMethodsOnProfession(profession));
  }

  @action isValid = (key: string) => {
    return !get(this.form.errors, key);
  };

  @action updateRecommendedByConsultant = (c: ConsultantCardFragment) => {
    if (c) {
      this.form.setValue("contacts.recommendedByConsultant", {
        label: c.Name,
        value: c._id
      });
    } else {
      this.form.setValue("contacts.recommendedByConsultant", null);
    }
  };

  @action addLanguageSkill = (name: string) => {
    this.form.push("languageSkills.items", { name, level: "" });
  };

  @action removeLanguage = (index: number) => {
    this.form.removeByIndex("languageSkills.items", index);
  };

  @action addEducation = () => {
    this.form.push("education.items", {});
  };

  @action removeEducation = (index: number) => {
    this.form.removeByIndex("education.items", index);
  };

  @action addWorkExperience = () => {
    this.form.push("workExperience.items", {});
  };

  @action removeWorkExperience = (index: number) => {
    this.form.removeByIndex("workExperience.items", index);
  };

  @action addProfessionStore = (profession: string, store: ExpertiseProfessionStore) => {
    this.professionStores[profession] = store;
  };

  getProfessionStore(profession: string) {
    return this.professionStores[profession];
  }

  @action addEmergencyContact = (value: ConsultantEmergencyContactsSectionItemInput) => {
    this.form.push("emergencyContacts.items", value);
  };

  @action removeEmergencyContacts = (index: number) => {
    this.form.removeByIndex("emergencyContacts.items", index);
  };

  @action updateWorkExperienceWorkPlace = (name: string, value: string) => {
    this.form.setValue(name, value);
  };

  @action updateLegalPersonType = (value: LegalPersonType) => {
    this.form.setValue("contactType.type", value);
    this.updateFormsValueByLegalPersonType(value);
  };

  @action updateClearingNumber = (value: number) => {
    this.form.setValue("contactType.clearingNumber", value);
  };

  @action updateInitialContact = (value: InitialContact) => {
    this.form.setValue("contacts.initialContact", value);
  };

  @action updateSelfRegisterOption = (value: SelfRegisterOption | null) => {
    this.form.setValue("contacts.selfRegisterOption", value);
  };

  @action updateMarketingConsentOption = (value: GDPROptionType) => {
    this.marketingConsentOption = value;

    if (!value || value === GDPROptionType.NotSpecified) {
      this.removeConsent(GDPRConsentType.Marketing);
      this.marketingConsentChannel = null;
    } else {
      this.setConsentOption(GDPRConsentType.Marketing, value);
    }
  };

  @action updateMailingEmailConsentOption = (value: GDPROptionType) => {
    this.mailingEmailConsentOption = value;

    if (!value || value === GDPROptionType.NotSpecified) {
      this.removeConsent(GDPRConsentType.JobMailingEmail);
      this.mailingEmailConsentChannel = null;
    } else {
      this.setConsentOption(GDPRConsentType.JobMailingEmail, value);
    }
  };

  @action updateMailingSmsConsentOption = (value: GDPROptionType) => {
    this.mailingSmsConsentOption = value;

    if (!value || value === GDPROptionType.NotSpecified) {
      this.removeConsent(GDPRConsentType.JobMailingSms);
      this.mailingSmsConsentChannel = null;
    } else {
      this.setConsentOption(GDPRConsentType.JobMailingSms, value);
    }
  };

  @action updateMarketingConsentChannel = (value: GDPRCommunicationChannel) => {
    this.marketingConsentChannel = value;
    this.setConsentChannel(GDPRConsentType.Marketing, value);
    this.form.validate();
  };

  @action updateMailingEmailConsentChannel = (value: GDPRCommunicationChannel) => {
    this.mailingEmailConsentChannel = value;
    this.setConsentChannel(GDPRConsentType.JobMailingEmail, value);
    this.form.validate();
  };

  @action updateMailingSmsConsentChannel = (value: GDPRCommunicationChannel) => {
    this.mailingSmsConsentChannel = value;
    this.setConsentChannel(GDPRConsentType.JobMailingSms, value);
    this.form.validate();
  };

  @action removeExpertiseProfession = (profession: string) => {
    const index = this.form.values.professions.findIndex(
      (x) => x.professionName === profession
    );

    const options = new OkCancelDialogOptions("Remove Profession.");
    store()
      .ui.confirmDialog.confirm(options)
      .then((value) => {
        if (value === "cancel") {
          return;
        }

        this.professionSelectionStore.removeProfession(profession);

        if (index > -1) {
          this.form.removeByIndex("professions", index);
        }

        delete this.professionStores[profession];

        this.form.values.professions.forEach((p, i) => {
          const professionStore = this.professionStores[p.professionName];
          if (professionStore) {
            professionStore.setStoreIndex(i);
          }
        });
      })
      .catch((err) => {
        store().snackbars.error(`Cannot remove Profession \n Reason:  ${err}`);
      });
  };

  @action setFile = (file: File) => {
    if (!file) {
      return;
    }
    if (file.type) {
      this.file = file;
    } else {
      store().snackbars.error(
        locale.getFileUploadError(this.importCvUploadConfig.acceptString)
      );
    }
  };

  @action selectFile = () => {
    store()
      .upload.requestFileUpload({
        multiple: false,
        acceptMimeTypes: this.importCvUploadConfig.acceptString
      })
      .then((files) => {
        files[0] && (this.file = files[0]);
      });
  };

  @action processFile = () => {
    if (!this.file) {
      return;
    }

    this.isFileProcessing = true;
    ConsultantEditor_ImportCV.func({
      consultantId: this.consultantId,
      file: this.file
    })
      .then(({ data: { importCV } }) => {
        this.consultantCVManager.importCV(importCV);
      })
      .catch((err) => {
        store().snackbars.error(`Cannot parse file\nReason: ${err.toString()}`);
      })
      .finally(() => {
        this.processedFile = this.file;
        this.file = null;
        this.isFileProcessing = false;
      });
  };

  fetchDeps = () => {
    let hospitals = this.form.values.workExperience?.items?.map((x) => x.customerRef?.Id);

    hospitals = compact(hospitals);

    if (hospitals?.length > 0) {
      ConsultantEditor_GetDepartments.func(
        { hospitalIds: hospitals },
        { fetchPolicy: "network-only" }
      ).then(({ data }) => {
        this.allDepartments = data.departments.items;
      });
    }
  };

  @action setHospital = (fieldName: string, hospital?: CustomerRefInput) => {
    if (!hospital) {
      this.form.setValue(`${fieldName}.customerRef`, null);
      this.form.setValue(`${fieldName}.departmentIds`, null);

      this.customValidate();
      return;
    }

    this.form.setValue(`${fieldName}.customerRef`, hospital);
    this.form.setValue(`${fieldName}.departmentIds`, null);
    this.fetchDeps();

    this.customValidate();
  };

  @action setEmploymentType = (fieldName: string, v?: LegalPersonType) => {
    // this.form.setValue(`${fieldName}.employmentType`, v);
    // this function is callback. Value is set underthehood on RadioGroup
    if (v === LegalPersonType.Employee) {
      this.form.setValue(`${fieldName}.competitors`, null);
    }
  };

  init = async () => {
    this.isLoading = true;

    try {
      if (this.consultantId) {
        const formDataQuery = await getForm.func({ consultantId: this.consultantId });
        const { consultantFormById, consultantById } = formDataQuery.data;

        this.form.reset({
          ...consultantFormById,
          isNew: false,
          responsiblePerson: consultantById.ResponsiblePerson
            ? {
                label: consultantById.ResponsiblePerson.Name,
                value: consultantById.ResponsiblePerson.PersonId
              }
            : null,
          professions: consultantById.expertise.professions || []
          // the query result is the server-side (output) shape of the form data
        } as ConsultantFormData);
      } else {
        const data: ConsultantFormData = {
          ...initialFormData,
          isNew: true
        };
        this.form.reset(data);
      }

      if (this.form.values.professions.length > 0) {
        let i = 0;

        for (const profession of this.form.values.professions) {
          const profStore = createProfessionStore(profession.professionName, i, this);
          this.addProfessionStore(profession.professionName, profStore);
          this.professionSelectionStore.selectedProfessions.push(
            profession.professionName
          );

          i++;
        }

        this.professionSelectionStore.setCurrentProfession(
          this.form.values.professions[0].professionName
        );
      }

      this.error = null;
      this.fetchDeps();
    } finally {
      this.isLoading = false;
    }
  };

  submit = () => {
    const formData = prepareDataForSubmit(omitDeep(this.form.values, "__new"));

    let consultantId = this.form.values.consultantId;

    if (this.isNew) {
      consultantId = createObjectID();
      formData.consultantId = consultantId;
    }

    this.form.disabled = true;

    return submitForm
      .func({
        consultantId,
        data: formData,
        file: this.processedFile
      })
      .then(({ data }) => {
        if (data.submitConsultantForm.OK) {
          // the mutation returns the server-side (output) shape of the form data
          this.form.reset(data.submitConsultantForm.ConsultantForm as ConsultantFormData);
          this.navigation.navigate(
            new ConsultantProfileLocation(data.submitConsultantForm.Consultant._id)
          );

          return;
        }

        if (!isEmpty(data.submitConsultantForm.Errors)) {
          store().snackbars.error(
            `Cannot save form\nReason: ${data.submitConsultantForm.Errors.join("\n")}`
          );
        }

        if (!isEmpty(data.submitConsultantForm.ValidationErrors)) {
          this.form.setErrors(data.submitConsultantForm.ValidationErrors);
        }
      })
      .catch((err) => {
        store().snackbars.error(`Cannot save form\nReason: ${err}`);
      })
      .finally(() => {
        this.form.disabled = false;
      });
  };

  close = () => {
    this.navigation.goBack();
  };

  ssnLookup = (country: string) => {
    const { values } = this.form;

    const value =
      country === "Sweden"
        ? this.form.values.personal.ssn
        : this.form.values.personal.norwegianSsn;

    this.setCurrentBisnodeLookup(values);

    this.bisnodeLookup
      .ssnLookup(country, value)
      .then((data) => {
        if (data && data.items && data.items.length > 0) {
          runInAction(() => {
            applyLookupResult(this.form, data.items || []);
            store().snackbars.success(locale.LookupApplied);
          });
        }
      })
      .catch((err) => {
        store().snackbars.error(`Lookup failed\nReason: ${err}`);
      });
  };
  phoneLookup = (country: string, phone: string) => {
    const { values } = this.form;

    this.setCurrentBisnodeLookup(values);

    this.bisnodeLookup
      .phoneLookup(country, phone)
      .then((data) => {
        runInAction(() => {
          // In case of cancel data.items will be undefined
          if (data.items) {
            applyLookupResult(this.form, data.items || []);
            store().snackbars.success(locale.LookupApplied);
          }
        });
      })
      .catch((err) => {
        store().snackbars.error(`Lookup failed\nReason: ${err}`);
      });
  };

  private setCurrentBisnodeLookup = (values) => {
    this.bisnodeLookup.setCurrentValues({
      AddressCity: values.address.city,
      AddressCountry: values.address.country,
      AddressStreetName: values.address.streetName,
      AddressZipCode: values.address.zipCode,
      FirstName: values.personal.firstName,
      LastName: values.personal.lastName,
      Phone: values.contacts.phone,
      CellPhone: values.contacts.cellPhone
    });
  };

  private updateFormsValueByLegalPersonType(l: LegalPersonType) {
    switch (l) {
      case LegalPersonType.Employee:
        this.form.setValue("contactType.businessAddress", null);
        this.form.setValue("contactType.ssn", null);
        this.form.setValue("contactType.companyName", null);
        this.form.setValue("contactType.employeeContractType", null);
        break;
      case LegalPersonType.Subcontractor:
        this.form.setValue("contactType.businessAddress", {
          city: "",
          country: "",
          streetName: "",
          zipCode: ""
        });
        this.form.setValue("contactType.ssn", "");
        this.form.setValue("contactType.companyName", "");
        break;
    }

    this.form.validate();
  }

  private customValidate() {
    const errors = {};

    // Adding errors for ConsulantExperiseInfo section tabbadge
    if (this.professionSelectionStore.selectedProfessions.length === 0) {
      set(errors, "expertise.profession", locale.ProfessionRequired);
    }

    if (this.isNew) {
      this.validateGDPR(errors);
    }

    return errors;
  }

  private validateGDPR(errors: object) {
    if (
      !this.marketingConsentOption ||
      (this.isMarketingConsentEnabled && !this.marketingConsentOption)
    ) {
      set(errors, "GDPRConsentRecords.marketingConsentOption", locale.Required);
    }

    if (this.isMarketingConsentEnabled && !this.marketingConsentChannel) {
      set(errors, "GDPRConsentRecords.marketingConsentChannel", locale.Required);
    }

    if (
      !this.mailingEmailConsentOption ||
      (this.isMailingEmailConsentEnabled && !this.mailingEmailConsentOption)
    ) {
      set(errors, "GDPRConsentRecords.mailingEmailConsentOption", locale.Required);
    }

    if (this.isMailingEmailConsentEnabled && !this.mailingEmailConsentChannel) {
      set(errors, "GDPRConsentRecords.mailingEmailConsentChannel", locale.Required);
    }

    if (
      !this.mailingSmsConsentOption ||
      (this.isMailingSmsConsentEnabled && !this.mailingSmsConsentOption)
    ) {
      set(errors, "GDPRConsentRecords.mailingSmsConsentOption", locale.Required);
    }

    if (this.isMailingSmsConsentEnabled && !this.mailingSmsConsentChannel) {
      set(errors, "GDPRConsentRecords.mailingSmsConsentChannel", locale.Required);
    }
  }

  private checkContactsOnDuplicating(event) {
    if (
      event.name === "contacts.email" ||
      event.name === "contacts.cellPhone" ||
      event.name === "contacts.phone" ||
      event.name === "personal.ssn"
    ) {
      ConsultantEditor_FindConsultantDuplicate.func({
        filter: {
          cellPhone: this.form.values.contacts.cellPhone,
          email: this.form.values.contacts.email,
          filterOut: this.consultantId,
          phone: this.form.values.contacts.phone,
          ssn: this.form.values.personal?.ssn || ""
        }
      })
        .then(({ data }) => {
          if (data) {
            this.contactsValidate = {
              duplicate: data.findConsultantDuplicate,
              similar: data.findConsultantSimilar
            };
          }
        })
        .catch((e) => {
          store().snackbars.error(`"Duplicate search" error .\nReason: ${e}`);
        })
        .finally(() => {
          this.isLoading = false;
        });
    }
  }

  private getConsent(type: GDPRConsentType): GDPRInput | undefined {
    return this.form.values.GDPRConsentRecords?.find((object) => object.type === type);
  }

  private setConsentOption(type: GDPRConsentType, option: GDPROptionType) {
    const existingConsent = this.getConsent(type);
    const isGranted = option === GDPROptionType.Yes;

    if (existingConsent) {
      existingConsent.granted = isGranted;
    } else {
      this.form.push("GDPRConsentRecords", [
        {
          type: type,
          granted: isGranted
        }
      ] as GDPRInput[]);
    }
  }

  private setConsentChannel(type: GDPRConsentType, channel: GDPRCommunicationChannel) {
    const existingConsent = this.getConsent(type);

    if (existingConsent) {
      existingConsent.communicationChannel = channel;
    } else {
      this.form.push("GDPRConsentRecords", [
        {
          type: type,
          communicationChannel: channel
        }
      ] as GDPRInput[]);
    }
  }

  private removeConsent(type: GDPRConsentType) {
    const consents: GDPRInput[] = this.form.values.GDPRConsentRecords;
    const filtered = consents.filter((consent) => consent.type !== type);

    this.form.setValue("GDPRConsentRecords", filtered);
  }
}
